import { cn } from "@lib/cn";
import { createPortal } from "react-dom";
import styles from "../styles/Skeleton.module.scss";

export const Skeleton = () => {
  return createPortal(
    <div className="tw-fixed tw-left-0 tw-top-0 tw-z-[99999] tw-mx-auto tw-h-screen tw-w-full tw-bg-blue-100">
      <Header />

      <div className="tw-flex">
        <Thumbnails />
        <div className="tw-w-full">
          <Settings />
          <SlideHeader />
          <Main />
        </div>
        <Menu />
      </div>
    </div>,
    document.body,
  );
};

const Header = () => {
  return (
    <div className="tw-flex tw-min-h-14 tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-solid tw-border-[#D9D7E0] tw-bg-white tw-px-3 tw-py-2">
      <div className="tw-flex tw-items-center">
        {/* Hamburger menu */}
        <span className={`${styles.gradient} tw-mr-3 tw-size-5 tw-rounded`} />
        {/* Logo */}
        <span
          className={`${styles.gradient} tw-mr-4 tw-h-5 tw-w-20 tw-rounded`}
        />
        {/* Doc title */}
        <span
          className={`${styles.gradient} tw-mr-2 tw-h-4 tw-w-36 tw-rounded`}
        />
        {/* Draft badge */}
        <span
          className={`${styles.gradient} tw-mr-4 tw-h-5 tw-w-12 tw-rounded-full`}
        />
        {/* Arrow icons */}
        <span className={`${styles.gradient} tw-mr-1 tw-size-4 tw-rounded`} />
        <span className={`${styles.gradient} tw-mr-4 tw-size-4 tw-rounded`} />
        {/* Refresh icon */}
        <span className={`${styles.gradient} tw-size-4 tw-rounded`} />
      </div>
      <div className="tw-flex tw-items-center tw-gap-2">
        {/* Add collaborator */}
        <span className={`${styles.gradient} tw-size-8 tw-rounded-full`} />
        {/* Icons */}
        <span className={`${styles.gradient} tw-size-5 tw-rounded`} />
        <span className={`${styles.gradient} tw-size-5 tw-rounded`} />
        <span className={`${styles.gradient} tw-size-5 tw-rounded`} />
        {/* Preview button */}
        <span className={`${styles.gradient} tw-h-8 tw-w-24 tw-rounded-lg`} />
        {/* Share button */}
        <span className={`${styles.gradient} tw-h-8 tw-w-16 tw-rounded-lg`} />
      </div>
    </div>
  );
};

const Settings = () => {
  const items = [
    { hasIcon: true, width: "tw-w-8" },
    { hasIcon: true, width: "tw-w-16" },
    { hasIcon: true, width: "tw-w-20" },
    { hasIcon: false, width: "tw-w-28" },
    { hasIcon: true, width: "tw-w-10" },
    { hasIcon: true, width: "tw-w-14" },
    { hasIcon: true, width: "tw-w-0" },
  ];

  return (
    <div className="tw-absolute tw-left-1/2 tw-top-[70px] tw-z-2 tw-ml-2 tw-flex tw-h-11 tw-w-fit -tw-translate-x-1/2 tw-items-center tw-rounded-lg tw-border tw-border-blue-100 tw-bg-white tw-px-3 tw-shadow-sm">
      <div className="tw-flex tw-items-center">
        {items.map((item, index) => (
          <div
            key={index}
            className={cn(
              "tw-flex tw-animate-[pulse_.8s_linear_infinite] tw-items-center tw-gap-1.5",
              {
                "tw-mr-0": index === items.length - 1,
              },
            )}
          >
            <span
              className={`${styles.gradient} tw-block tw-size-4 tw-rounded`}
            />
            {item.width !== "tw-w-0" && (
              <span
                className={`${styles.gradient} tw-block tw-h-3 ${item.width} tw-rounded`}
              />
            )}
            {index !== items.length - 1 && (
              <span className="tw-mx-2 tw-block tw-h-4 tw-w-[1px] tw-bg-blue-100" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SlideHeader = () => {
  return (
    <div className="tw-flex tw-animate-[pulse_.8s_linear_infinite] tw-items-center tw-gap-3 tw-pb-1 tw-pl-4 tw-pt-16">
      {/* Slide number */}
      <span className={`${styles.gradient} tw-h-4 tw-w-12 tw-rounded`} />
      {/* Hide slide */}
      <span className={`${styles.gradient} tw-size-4 tw-rounded`} />
      <span className={`${styles.gradient} tw-h-3 tw-w-16 tw-rounded`} />
      {/* Action icons */}
      <span className={`${styles.gradient} tw-size-4 tw-rounded`} />
      <span className={`${styles.gradient} tw-size-4 tw-rounded`} />
      <span className={`${styles.gradient} tw-size-4 tw-rounded`} />
      <span className={`${styles.gradient} tw-size-4 tw-rounded`} />
    </div>
  );
};

const Thumbnails = () => {
  const array = Array.from(Array(8), (_, x) => x);
  return (
    <div className="tw-relative tw-size-full tw-h-[calc(100vh_-_56px)] tw-w-[152px] tw-shrink-0 tw-border-r tw-border-solid tw-border-[#D9D7E0] tw-bg-blue-50 tw-backdrop-blur-[2px] tw-transition-all tw-duration-300">
      {/* Add slide button */}
      <div className="tw-animate-[pulse_.8s_linear_infinite] tw-p-3 tw-pb-2">
        <span
          className={`${styles.gradient} tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-center tw-rounded-lg`}
        />
      </div>
      {/* Slide thumbnails */}
      <div className="tw-h-[calc(100vh_-_200px)] tw-overflow-hidden tw-px-3">
        {array.map((index) => {
          return (
            <div
              key={index}
              className="tw-mb-2 tw-flex tw-animate-[pulse_.8s_linear_infinite] tw-items-center"
            >
              <span
                className={`${styles.gradient} tw-mr-1 tw-block tw-h-3 tw-w-3 tw-shrink-0 tw-rounded tw-text-[10px]`}
              />
              <span
                className={`${styles.gradient} tw-h-[52px] tw-w-full tw-rounded`}
              />
            </div>
          );
        })}
      </div>
      {/* Slides limit bar */}
      <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-animate-[pulse_.8s_linear_infinite] tw-border-t tw-border-solid tw-border-[#D9D7E0] tw-p-3">
        <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
          <span className={`${styles.gradient} tw-h-3 tw-w-16 tw-rounded`} />
          <span className={`${styles.gradient} tw-h-3 tw-w-8 tw-rounded`} />
        </div>
        <span
          className={`${styles.gradient} tw-block tw-h-1.5 tw-w-full tw-rounded-full`}
        />
        <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between">
          <span
            className={`${styles.gradient} tw-block tw-size-5 tw-rounded`}
          />
          <span
            className={`${styles.gradient} tw-block tw-size-5 tw-rounded`}
          />
        </div>
      </div>
    </div>
  );
};

const Main = () => {
  return (
    <div className="tw-w-full tw-animate-skeleton-loading tw-px-4 tw-pb-4 tw-pt-2">
      <span className="tw-block tw-h-[calc(100vh_-_168px)] tw-w-full tw-rounded-xl tw-bg-blue-200/50 tw-animate-skeleton-loading" />
    </div>
  );
};

const Menu = () => {
  const topIcons = Array.from(Array(7), (_, x) => x);
  const bottomIcons = Array.from(Array(4), (_, x) => x);

  return (
    <div className="tw-flex tw-w-14 tw-flex-col tw-border-l tw-border-solid tw-border-[#D9D7E0] tw-bg-white tw-justify-between tw-items-center">
      <div className="tw-space-y-2 tw-py-2">
        {topIcons.map((index) => (
          <span
            key={index}
            className="tw-rounded-lg tw-bg-blue-100/50 tw-size-8 tw-block tw-animate-skeleton-loading"
          />
        ))}
      </div>
      <div className="tw-space-y-2 tw-py-2">
        {bottomIcons.map((index) => (
          <span
            key={index}
            className="tw-rounded-lg tw-bg-blue-100/50 tw-size-8 tw-block tw-animate-skeleton-loading"
          />
        ))}
      </div>
    </div>
  );
};
